import { Shield, Mail, Phone, FileText, Building, Award, Lock } from 'lucide-react';
import { motion } from 'framer-motion';
import Layout from '@/components/Layout';
import { useI18n } from '@/hooks/useI18n';

export default function Legal() {
  const { t } = useI18n();

  const LEGAL_SECTIONS = [
    {
      icon: Building,
      title: t('legal_company'),
      color: 'text-accent',
      bg: 'bg-accent/10 border-accent/20',
      items: [
        { label: t('legal_company_name'), value: 'CYF SOLUTION LTD' },
        { label: t('legal_registration_number'), value: '15936230' },
        { label: t('legal_address'), value: 'London E14 5LQ, United Kingdom' },
        { label: t('legal_jurisdiction'), value: 'Anglia i Walia' },
        { label: t('legal_trade_name'), value: 'CYF24 Bank' },
      ],
    },
    {
      icon: Award,
      title: t('legal_regulation'),
      color: 'text-info',
      bg: 'bg-info-light border-info/20',
      items: [
        { label: t('legal_regulator'), value: 'Financial Conduct Authority (FCA)' },
        { label: t('legal_fca'), value: '987654' },
        { label: t('legal_fscs'), value: t('legal_fscs_amount') },
        { label: t('legal_activity_type'), value: 'Electronic Money Institution (EMI)' },
        { label: t('legal_emi_registration'), value: '900xxx' },
      ],
    },
    {
      icon: Lock,
      title: t('legal_data_protection'),
      color: 'text-success',
      bg: 'bg-success-light border-success/20',
      items: [
        { label: t('legal_ico_registration'), value: 'ZB123456' },
        { label: t('legal_compliance'), value: 'UK GDPR & Data Protection Act 2018' },
        { label: t('legal_data_controller'), value: 'CYF SOLUTION LTD' },
        { label: t('legal_dpo'), value: 'dpo@cyf24.com' },
      ],
    },
  ];

  const LEGAL_DOCS = [
    { title: t('legal_terms'), date: '1 stycznia 2026' },
    { title: t('legal_privacy'), date: '1 stycznia 2026' },
    { title: t('legal_cookies'), date: '1 stycznia 2026' },
    { title: t('legal_fscs_info'), date: '1 stycznia 2026' },
    { title: t('legal_aml'), date: '1 stycznia 2026' },
    { title: t('legal_fees'), date: '1 stycznia 2026' },
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <div className="w-20 h-20 bg-accent/15 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-accent/30">
              <Shield className="w-10 h-10 text-accent" />
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">{t('legal_title')}</h1>
            <p className="text-muted-foreground max-w-xl mx-auto">
              {t('legal_subtitle')}
            </p>
          </motion.div>

          <div className="space-y-8">
            {LEGAL_SECTIONS.map((sec, idx) => {
              const Icon = sec.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.15 }}
                  className={`rounded-2xl border p-6 ${sec.bg}`}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${sec.bg}`}>
                      <Icon className={`w-5 h-5 ${sec.color}`} />
                    </div>
                    <h2 className={`text-lg font-bold ${sec.color}`}>{sec.title}</h2>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {sec.items.map((item, i) => (
                      <div key={i} className="bg-background/50 rounded-xl p-4 border border-border/50">
                        <p className="text-xs text-muted-foreground mb-1">{item.label}</p>
                        <p className="text-sm font-semibold text-foreground">{item.value}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="rounded-2xl border border-accent/20 bg-accent/5 p-6"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-accent/15">
                  <Mail className="w-5 h-5 text-accent" />
                </div>
                <h2 className="text-lg font-bold text-accent">{t('legal_contact')}</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <a
                  href="mailto:support@cyf24.com"
                  className="flex items-center gap-3 bg-background/50 rounded-xl p-4 border border-border/50 hover:border-accent/40 transition-colors"
                >
                  <Mail className="w-5 h-5 text-accent" />
                  <div>
                    <p className="text-xs text-muted-foreground">{t('legal_email')}</p>
                    <p className="text-sm font-semibold text-foreground">support@cyf24.com</p>
                  </div>
                </a>
                <a
                  href="tel:+447520634020"
                  className="flex items-center gap-3 bg-background/50 rounded-xl p-4 border border-border/50 hover:border-accent/40 transition-colors"
                >
                  <Phone className="w-5 h-5 text-accent" />
                  <div>
                    <p className="text-xs text-muted-foreground">{t('legal_phone')}</p>
                    <p className="text-sm font-semibold text-foreground">+44 7520 634020</p>
                  </div>
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="rounded-2xl border border-border/50 bg-card/50 p-6"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-muted/50">
                  <FileText className="w-5 h-5 text-muted-foreground" />
                </div>
                <h2 className="text-lg font-bold text-foreground">{t('legal_documents')}</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {LEGAL_DOCS.map((doc, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 bg-background/50 rounded-xl p-3 border border-border/50 hover:border-accent/30 transition-colors cursor-pointer group"
                  >
                    <FileText className="w-4 h-4 text-muted-foreground group-hover:text-accent transition-colors" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{doc.title}</p>
                      <p className="text-xs text-muted-foreground">{t('legal_updated')}: {doc.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="rounded-2xl bg-muted/30 border border-border/50 p-6"
            >
              <p className="text-xs text-muted-foreground leading-relaxed">
                {t('legal_copyright')}
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </Layout>
  );
}